import PropTypes from 'prop-types';

import { ContainerProps as MuiContainerProps } from '@mui/material/Container';
import { styled } from '@mui/material/styles';
import Helmet from '../../components/Helmet';
import { Box, Stack, Typography, Button } from '@mui/material';
import Image from '../../assets/img/Banner.png';
import {
  Filter,
  HomeIcon,
  HousingIcon,
  Pagination,
  Sheet,
  UserProfile,
} from '@classity-edtech/ui-base/dist/components/Displays';
import { CertificationCategory, CountryCode } from '@classity-edtech/platform-utils';
import { useRef, useEffect, useState, useMemo } from 'react';
import useAppMediaQuery from '@classity-edtech/ui-base/dist/hooks/useMediaQuery';
import GoogleButton from '../../components/GoogleButton';
import {
  CircularProgress,
  generateImageAssestUrl,
  getCountry,
  getSessionStartDate,
} from '@classity-edtech/ui-base';
import { useGetSubjectsQuery } from '../../services/subjects';
import { useGetProductsQuery } from '../../services/products';
import { Link, useNavigate } from 'react-router-dom';
import { globalColours } from '@classity-edtech/ui-base/dist/style/source';
import { Footer } from '../../components/SEO/Footer';
import { Pitch } from '../../components/SEO/Pitch';
import { extractFooterContent } from '../../utils/extractContents';
import { useGetPageContentQuery } from '../../services/seoContent';
import Header from '../../components/Header';
import { webPaths } from '../../routes/paths';
import { ArrowLeftIcon } from '@mui/x-date-pickers';
import { useCurriculumQuery } from '../../services/curriculums';
import dayjs from 'dayjs';
import { Tabs } from '@classity-edtech/ui-base/dist/components/Displays/Tabs/Tabs';
import { User } from '../../services/models/profile';
import { useLanguagesQuery } from '../../services/languages';
import { LeftSidebarFilter } from '../../components/LeftSidebarFilter';
import { ProductCard } from '../../components/ProductCard';

export interface ProductProps extends MuiContainerProps {}

const description =
  'The second term focuses on consolidating first-term foundations in literacy and numeracy while advancing hands-on learning in Science & Technology, ICT, Agricultural and vocational subjects, with Civic Education and Nigerian languages reinforcing cultural values and citizenship.';

export const Curriculum = ({ className }: ProductProps) => {
  const screens = useAppMediaQuery();
  const navigate = useNavigate();

  const { data, isLoading: fetchingContent } = useGetPageContentQuery({
    tag: 'about_us',
    contentType: 'page',
  });

  const { data: curriculum } = useCurriculumQuery({
    code: CountryCode.NG,
    fields: 'identification,gradeLevels,educationalStages,session',
    sessionStart: new Date(getSessionStartDate()).toISOString(),
  });

  const gradeLevels = useMemo(() => {
    if (!curriculum?.gradeLevels) return { secondary: [], primary: [] };

    const filteredGrades = curriculum.gradeLevels.filter(grade => {
      const equivalent = grade.equivalent;
      if (!equivalent) return false;

      const yearMatch = equivalent.match(/Y(\d+)/);
      if (!yearMatch) return false;

      const year = parseInt(yearMatch[1]);
      return year >= 1 && year <= 12;
    });

    const primary = filteredGrades
      .filter(grade => {
        const yearMatch = grade.equivalent?.match(/Y(\d+)/);
        const year = yearMatch ? parseInt(yearMatch[1]) : 0;
        return year >= 1 && year <= 6;
      })
      .map(grade => {
        const yearMatch = grade.equivalent?.match(/Y(\d+)/);
        const year = yearMatch ? parseInt(yearMatch[1]) : 0;
        return {
          id: grade._id,
          label: `PRY ${year}`,
        };
      });

    const secondary = filteredGrades
      .filter(grade => {
        const yearMatch = grade.equivalent?.match(/Y(\d+)/);
        const year = yearMatch ? parseInt(yearMatch[1]) : 0;
        return year >= 7 && year <= 12;
      })
      .map(grade => {
        const yearMatch = grade.equivalent?.match(/Y(\d+)/);
        const year = yearMatch ? parseInt(yearMatch[1]) : 0;

        if (year >= 7 && year <= 9) {
          return {
            id: grade._id,
            label: `JSS ${year - 6}`,
          };
        } else if (year >= 10 && year <= 12) {
          return {
            id: grade._id,
            label: `SSS ${year - 9}`,
          };
        }
        return { id: grade._id, label: grade.equivalent || '' };
      })
      .filter(grade => grade.label);

    return { secondary, primary };
  }, [curriculum?.gradeLevels]);

  const [selectedGradeLevels, setSelectedGradeLevels] = useState<string[]>([]);
  const [selectedSubjectsByGroup, setSelectedSubjectsByGroup] = useState<Record<string, string[]>>(
    {},
  );
  const [selectedTimeOfDay, setSelectedTimeOfDay] = useState<string[]>([
    'EARLY_MORNING',
    'MORNING',
    'AFTERNOON',
    'EVENING',
  ]);
  const [sortBy, setSortBy] = useState<'EARLIEST' | 'LATEST'>('EARLIEST');
  const [expandedSections, setExpandedSections] = useState({
    secondary: true,
    primary: true,
    timeOfDay: true,
  });
  const { data: subjects = [], isLoading: loadingSubjects } = useGetSubjectsQuery({});
  const subjectGroups = useMemo(() => {
    if (!subjects?.length) return [];

    const groupDefinitions = {
      Mathematics: ['Mathematics', 'General Mathematics', 'Further Mathematics'],
      Languages: [
        'English Studies',
        'English Language',
        'English Literature',
        'Yoruba',
        'Hausa',
        'Igbo',
        'French',
        'Chinese',
      ],
      Sciences: [
        'Basic Science',
        'Basic Science & Technology',
        'Digital Technologies',
        'Intermediate Science',
        'Biology',
        'Chemistry',
        'Physics',
      ],
      Business: ['Accounting', 'Economics', 'Commerce', 'Marketing'],
    };

    const groupMap: Record<string, { id: string; name: string }[]> = {
      Mathematics: [],
      Languages: [],
      Sciences: [],
      Business: [],
    };

    subjects.forEach(subject => {
      const subjectName = subject.name.text;
      const subjectData = { id: subject.id, name: subject.name.text };

      Object.entries(groupDefinitions).forEach(([groupName, groupSubjects]) => {
        if (
          groupSubjects.some(
            groupSubject =>
              subjectName.toLowerCase().includes(groupSubject.toLowerCase()) ||
              groupSubject.toLowerCase().includes(subjectName.toLowerCase()),
          )
        ) {
          groupMap[groupName as keyof typeof groupMap].push(subjectData);
        }
      });
    });

    return Object.entries(groupMap)
      .filter(([_, subjects]) => subjects.length > 0)
      .map(([name, subjects]) => ({ name, subjects }));
  }, [subjects]);

  useEffect(() => {
    if (curriculum?.gradeLevels && selectedGradeLevels.length === 0) {
      const gradeIds = curriculum.gradeLevels
        .filter(grade => {
          const equivalent = grade.equivalent;
          if (!equivalent) return false;

          const yearMatch = equivalent.match(/Y(\d+)/);
          if (!yearMatch) return false;

          const year = parseInt(yearMatch[1]);
          return year >= 1 && year <= 12;
        })
        .map(grade => grade._id)
        .reverse();

      setSelectedGradeLevels(gradeIds);
    }
  }, [curriculum?.gradeLevels, selectedGradeLevels.length]);

  useEffect(() => {
    if (subjectGroups.length > 0 && Object.keys(selectedSubjectsByGroup).length === 0) {
      const initialSelection: Record<string, string[]> = {};
      subjectGroups.forEach(group => {
        initialSelection[group.name] = group.subjects.map(subject => subject.id);
      });
      setSelectedSubjectsByGroup(initialSelection);
    }
  }, [subjectGroups, selectedSubjectsByGroup]);

  const scrollContainerRefs = useRef<Record<string, HTMLElement | null>>({});

  const [openSidebar, setOpenSidebar] = useState(false);

  const toggleOpenSidebar = () => {
    setOpenSidebar(!openSidebar);
  };

  const isSmallScreen = screens.isMobile || screens.isTablet;
  const [userProfile, setUserProfile] = useState<any>();
  const [expandedText, setExpandedText] = useState('');
  const [hasOverflow, setHasOverflow] = useState(false);
  const textRef = useRef<HTMLElement>(null);
  const [mathsPage, setMathsPage] = useState(1);
  const [languagesPage, setLanguagesPage] = useState(1);
  const [sciencesPage, setSciencesPage] = useState(1);
  const { data: currLanguages, isLoading: isLanguagesLoading } = useLanguagesQuery();
  const {
    data: mathsProduct,
    isLoading: loadingMathsProducts,
    isError: isMathsError,
  } = useGetProductsQuery({
    limit: 10,
    page: mathsPage,
    gradeLevelIds: selectedGradeLevels?.join(','),
    subjectIds: selectedSubjectsByGroup?.['Mathematics']?.join(','),
    timeOfDay: selectedTimeOfDay.join(','),
    sortBy,
  });
  const {
    data: languagesProduct,
    isLoading: loadingLanguagesProducts,
    isError: isLanguagesError,
  } = useGetProductsQuery({
    limit: 10,
    page: languagesPage,
    gradeLevelIds: selectedGradeLevels?.join(','),
    subjectIds: selectedSubjectsByGroup?.['Languages']?.join(','),
    timeOfDay: selectedTimeOfDay.join(','),
    sortBy,
  });
  const {
    data: sciencesProduct,
    isLoading: loadingSciencesProducts,
    isError: isSciencesError,
  } = useGetProductsQuery({
    limit: 10,
    page: sciencesPage,
    gradeLevelIds: selectedGradeLevels?.join(','),
    subjectIds: selectedSubjectsByGroup?.['Sciences']?.join(','),
    timeOfDay: selectedTimeOfDay.join(','),
    sortBy,
  });

  const handlePageChange = (groupName: string, newPage: number) => {
    if (groupName === 'Mathematics') {
      setMathsPage(newPage);
    } else if (groupName === 'Languages') {
      setLanguagesPage(newPage);
    } else if (groupName === 'Sciences') {
      setSciencesPage(newPage);
    }
  };

  const getCurrentPage = (groupName: string) => {
    if (groupName === 'Mathematics') {
      return mathsPage;
    } else if (groupName === 'Languages') {
      return languagesPage;
    } else if (groupName === 'Sciences') {
      return sciencesPage;
    }
  };

  const getProducts = (subjectGroup: string) => {
    if (subjectGroup === 'Mathematics')
      return {
        data: !isMathsError ? mathsProduct : [],
        isLoading: loadingMathsProducts,
        isError: isMathsError,
      };
    if (subjectGroup === 'Languages')
      return {
        data: !isLanguagesError ? languagesProduct : [],
        isLoading: loadingLanguagesProducts,
        isError: isLanguagesError,
      };
    if (subjectGroup === 'Sciences')
      return {
        data: !isSciencesError ? sciencesProduct : [],
        isLoading: loadingSciencesProducts,
        isError: isSciencesError,
      };
  };

  const timeOfDayOptions = [
    { id: 'EARLY_MORNING', label: 'Early morning' },
    { id: 'MORNING', label: 'Morning' },
    { id: 'AFTERNOON', label: 'Afternoon' },
    { id: 'EVENING', label: 'Evening' },
  ];

  const handleNavigateToClass = (slug: string) => {
    navigate(`/${webPaths.classes}/${slug}`);
  };

  const handleSheetOpen = (text: string) => {
    setExpandedText(text);
  };

  const handleCloseSheet = () => setExpandedText('');

  const handleSetUserProfile = (user: User) => {
    const profile = user?.profile;

    const userProfile = () => {
      const gradeLevel =
        profile?.preference?.gradeLevels?.map(
          g => curriculum?.educationalStages?.find(c => c._id === g)?.stage?.text,
        ) || [];
      const languages = user?.preferences?.knowsLanguages?.map(
        l => currLanguages?.find(lang => lang?.code === l)?.name?.text,
      );
      const experiences =
        profile?.experiences?.map(experience => {
          const institution = curriculum?.institutions?.find(
            inst => inst?._id === experience?.organizationId,
          );

          return {
            institution: institution?.name?.text,
            location:
              `${institution?.addresses?.[0]?.township || ''} ${getCountry(institution?.addresses?.[0]?.country || CountryCode.NG)}`.trim(),
            date: `${dayjs(experience.from).format('MMM YYYY')} - ${dayjs(experience.to).format('MMM YYYY')}`,
          };
        }) || [];

      const languageCert = profile?.certifications?.find(
        cert => cert.category === CertificationCategory.LANGUAGE,
      );
      const trainingCert = profile?.certifications?.find(
        cert => cert.category === CertificationCategory.TRAINING,
      );
      const academicCert = profile?.certifications?.find(
        cert => cert.category === CertificationCategory.ACADEMIC,
      );

      return {
        profile: {
          name: `${profile?.firstname || ''} ${profile?.lastname || ''}`.trim(),
          src: generateImageAssestUrl(profile?.avatar?.contentUrl, 1),
          rating: 4.9,
          followers: 14,
          badge: 'Classity',
          countryCode: CountryCode.NG,
          followAction: () => {},
          isBadgeActive: true,
        },
        bio: profile?.bio || '',
        bioContent: (
          <Box
            component='img'
            sx={{
              aspectRatio: '9/5',
              borderRadius: '8px',
              height: '150px',
              background: globalColours['$global-colour-palette-gray'],
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              objectFit: 'cover',
            }}
            alt=''
            src={generateImageAssestUrl(profile?.avatar?.contentUrl, 1)}
          />
        ),
        gradeLevels: gradeLevel,
        languages: languages,
        qualifications: trainingCert
          ? [
              {
                title: 'Training Qualification',
                institution: trainingCert.issuedByName,
                date: trainingCert.periodOfStudyTo
                  ? new Date(trainingCert.periodOfStudyTo).toLocaleDateString()
                  : 'N/A',
              },
            ]
          : [],
        experiences,
        englishProficiencies: languageCert
          ? [
              {
                institution: languageCert.issuedByName,
              },
            ]
          : [],
        credentials: academicCert
          ? [
              {
                institution: academicCert.issuedByName,
                title: 'Academic Qualification',
                date: academicCert.periodOfStudyTo
                  ? new Date(academicCert.periodOfStudyTo).toLocaleDateString()
                  : 'N/A',
              },
            ]
          : [],
        tabs: isSmallScreen
          ? []
          : [
              {
                label: 'Profile',
                active: true,
                action: () => {},
              },
              {
                label: 'Reviews',
                active: false,
                action: () => {},
              },
            ],
      };
    };

    setUserProfile(userProfile());
  };

  const clearUserProfile = () => {
    setUserProfile(undefined);
  };

  const toggleGradeLevel = (gradeId: string) => {
    setSelectedGradeLevels(prev =>
      prev.includes(gradeId) ? prev.filter(id => id !== gradeId) : [...prev, gradeId],
    );
  };

  const toggleTimeOfDay = (timeId: string) => {
    setSelectedTimeOfDay(prev =>
      prev.includes(timeId) ? prev.filter(id => id !== timeId) : [...prev, timeId],
    );
  };

  const selectAllGradeLevels = (category: 'secondary' | 'primary') => {
    const categoryIds = gradeLevels[category].map(g => g.id);
    const allSelected = categoryIds.every(id => selectedGradeLevels.includes(id));

    if (allSelected) {
      setSelectedGradeLevels(prev => prev.filter(id => !categoryIds.includes(id)));
    } else {
      setSelectedGradeLevels(prev => [...new Set([...prev, ...categoryIds])]);
    }
  };

  const selectAllTimeOfDay = () => {
    const allTimeIds = timeOfDayOptions.map(t => t.id);
    const allSelected = allTimeIds.every(id => selectedTimeOfDay.includes(id));

    if (allSelected) {
      setSelectedTimeOfDay([]);
    } else {
      setSelectedTimeOfDay(allTimeIds);
    }
  };

  const handleSubjectGroupFilter = (groupName: string, subjectId: string) => {
    setSelectedSubjectsByGroup(prev => {
      const currentGroupSubjects = prev[groupName] || [];
      const isCurrentlySelected = currentGroupSubjects.includes(subjectId);

      if (isCurrentlySelected && currentGroupSubjects.length === 1) {
        return prev;
      }

      return {
        ...prev,
        [groupName]: isCurrentlySelected
          ? currentGroupSubjects.filter(s => s !== subjectId)
          : [...currentGroupSubjects, subjectId],
      };
    });
  };

  const handleScroll = (groupName: string, direction: 'left' | 'right') => {
    const container = scrollContainerRefs.current[groupName];
    if (container) {
      const scrollAmount = 320;
      container.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth',
      });
    }
  };

  const toggleSection = (section: keyof typeof expandedSections) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const extractPitchSection = () => {
    const content = data?.sections?.[4];

    return {
      title: content?.fields?.title ?? '',
      description: content?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
      titleImageUrl:
        content?.fields?.list?.[content?.fields?.list?.length - 1]?.fields?.media?.fields?.icon
          ?.fields?.file?.url ?? '',
      items:
        content?.fields?.list?.slice(0, content?.fields?.list?.length - 1)?.map((item: any) => ({
          title: item?.fields?.title ?? '',
          description: item?.fields?.description?.content?.[0]?.content?.[0]?.value ?? '',
          imageUrl: item?.fields?.media?.fields?.icon?.fields?.file?.url ?? '',
        })) ?? [],
    };
  };

  useEffect(() => {
    if (textRef.current) {
      setHasOverflow(textRef.current.scrollHeight > textRef.current.clientHeight);
    }
  }, []);

  return (
    <>
      <Helmet />

      {fetchingContent ? (
        <Box
          sx={{
            width: '100%',
            height: '80vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <>
          <GoogleButton useOneTap />

          <Header toggleOpenSidebar={toggleOpenSidebar} isExpanded />

          <Box sx={{ width: '100%' }}>
            <Box
              sx={{
                display: { xs: 'none', md: 'flex' },
                gap: 1.5,
                alignItems: 'center',
                height: '48px',
                mb: 7,
                ml: { xs: 0, lg: '212px' },
                px: { xs: 0, md: 4 },
              }}
            >
              <Link to={webPaths.root} style={{ textDecoration: 'none' }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    color: globalColours['$global-colour-content-primary'],
                    '&:hover': {
                      color: globalColours['$global-colour-interactive-control-hover'],
                    },
                  }}
                >
                  <HomeIcon sx={{ width: 14, height: 14 }} />
                  <Typography variant='body3'>Home</Typography>
                </Box>
              </Link>
            </Box>

            <Box sx={{ display: 'flex', gap: 4 }}>
              <LeftSidebarFilter
                openSidebar={openSidebar}
                toggleOpenSidebar={toggleOpenSidebar}
                gradeLevels={gradeLevels}
                selectedGradeLevels={selectedGradeLevels}
                toggleGradeLevel={toggleGradeLevel}
                selectAllGradeLevels={selectAllGradeLevels}
                timeOfDayOptions={timeOfDayOptions}
                selectedTimeOfDay={selectedTimeOfDay}
                toggleTimeOfDay={toggleTimeOfDay}
                selectAllTimeOfDay={selectAllTimeOfDay}
                expandedSections={expandedSections}
                toggleSection={toggleSection}
              />
              {fetchingContent || loadingSubjects ? (
                <Box
                  sx={{
                    width: '100%',
                    height: '80vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress />
                </Box>
              ) : (
                <Box>
                  <Box
                    sx={{
                      px: 2,
                      py: 3,
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      gap: '10px',
                      width: '100%',
                    }}
                  >
                    <Stack
                      sx={{
                        gap: { xs: 1, md: 1.5 },
                        minWidth: { xs: 'fit-content', md: '400px' },
                        flex: 0.4,
                      }}
                    >
                      <Typography variant={isSmallScreen ? 'subtitle3' : 'subtitle1'}>
                        Second term of the 2025/2026 academic session
                      </Typography>
                      <Box>
                        <Typography
                          ref={textRef}
                          variant={isSmallScreen ? 'body3' : 'body1'}
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 5,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                          }}
                        >
                          {description}
                        </Typography>
                        {hasOverflow && (
                          <Button
                            variant='text'
                            size='small'
                            onClick={() => handleSheetOpen(description)}
                            sx={{
                              p: 0,
                              mt: 0.5,
                              textTransform: 'none',
                              textDecoration: 'underline',
                            }}
                          >
                            Read more
                          </Button>
                        )}
                      </Box>
                    </Stack>
                    <Box sx={{ width: '100%' }}>
                      <Box sx={{ width: '100%', alignSelf: 'flex-end' }}>
                        <Box
                          component='img'
                          sx={{
                            height: { xs: '280px', md: 'auto' },
                            width: '100%',
                            borderRadius: { xs: '16px', md: '30px' },
                            objectFit: 'cover',
                          }}
                          alt=''
                          src={Image}
                        />
                      </Box>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          py: 1,
                          px: { xs: 0, md: 6.5 },
                          gap: 1.5,
                          mx: { xs: 'auto', md: '0' },
                          width: 'fit-content',
                        }}
                      >
                        <HousingIcon sx={{ width: '20px', height: '20px' }} />
                        <Typography variant={'body1SemiBold'}>Nigerian Curriculum</Typography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ height: '96px', display: { xs: 'none', md: 'block' } }} />

                  <Box sx={{ px: 2, pb: 3 }}>
                    <Box
                      sx={{
                        display: { xs: 'flex', md: 'none' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: `solid 1px ${globalColours['$global-colour-border-overlay']}`,
                        gap: 0.5,
                        color: globalColours['$global-colour-interactive-primary-default'],
                        position: 'relative',
                        width: '100%',
                        p: 1,
                        height: '64px',
                        cursor: 'pointer',
                        borderRadius: '8px',
                      }}
                      onClick={toggleOpenSidebar}
                    >
                      <Filter sx={{ width: '16px', height: '16px' }} />
                      <Typography variant='body3'>Filter</Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      maxWidth: {
                        xs: 'calc(100dvw - 32px)',
                        lg: 'calc(100dvw - 240px)',
                        xl: '1400px',
                      },
                      mx: 'auto',
                    }}
                  >
                    {subjectGroups.map((group, groupIndex) => {
                      const selectedGroupSubjects = selectedSubjectsByGroup[group.name] || [];

                      return (
                        <Box key={group.name} sx={{ mb: 6 }}>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'flex-end',
                              gap: 1.5,
                              justifyContent: 'space-between',
                              borderBottom: `1px solid ${globalColours['$global-colour-border-neutral']}`,
                              flexWrap: { xs: 'wrap', lg: 'nowrap' },
                            }}
                          >
                            <Typography variant='subtitle4'>{group.name}</Typography>

                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 0.5,
                                justifyContent: 'flex-end',
                              }}
                            >
                              <Box
                                sx={{
                                  maxWidth: { xs: '100%', lg: '50dvw' },
                                  ml: 'auto',
                                }}
                              >
                                <Tabs
                                  sx={{
                                    '& .scroll-left': {
                                      top: '9px',
                                    },
                                    '& .scroll-right': {
                                      top: '9px',
                                    },
                                  }}
                                  tabElements={
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        gap: 1,
                                        flexWrap: { xs: 'wrap', lg: 'nowrap' },
                                        py: 1,
                                      }}
                                    >
                                      {group.subjects.map(subject => {
                                        const isSelected = selectedGroupSubjects.includes(
                                          subject.id,
                                        );
                                        return (
                                          <Box
                                            key={subject.id}
                                            onClick={() =>
                                              handleSubjectGroupFilter(group.name, subject.id)
                                            }
                                            sx={{
                                              px: 2,
                                              py: 1,
                                              borderRadius: '20px',
                                              border: `1px solid ${
                                                isSelected
                                                  ? globalColours[
                                                      '$global-colour-interactive-accent-active'
                                                    ]
                                                  : globalColours['$global-colour-border-neutral']
                                              }`,
                                              backgroundColor: 'transparent',
                                              color: isSelected
                                                ? globalColours[
                                                    '$global-colour-interactive-accent-active'
                                                  ]
                                                : globalColours['$global-colour-text-primary'],
                                              '&:hover': {
                                                backgroundColor:
                                                  globalColours[
                                                    '$global-colour-interactive-accent-hover'
                                                  ],
                                                color:
                                                  globalColours['$global-colour-base-contrast'],
                                              },
                                              cursor: 'pointer',
                                              transition: 'all 0.2s ease',
                                            }}
                                          >
                                            <Typography variant='body3SemiBold'>
                                              {subject.name}
                                            </Typography>
                                          </Box>
                                        );
                                      })}
                                    </Box>
                                  }
                                />
                              </Box>

                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 2,
                                  justifyContent: 'flex-end',
                                  flexWrap: { xs: 'wrap', lg: 'nowrap' },
                                }}
                              >
                                <Typography variant='body3'>
                                  {getProducts(group?.name)?.data?.length} class
                                  {getProducts(group?.name)?.data?.length !== 1 ? 'es' : ''} found
                                </Typography>

                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                    cursor: 'pointer',
                                    width: 'fit-content',
                                  }}
                                  onClick={() =>
                                    setSortBy(sortBy === 'EARLIEST' ? 'LATEST' : 'EARLIEST')
                                  }
                                >
                                  <Typography variant='body3'>Sort by:</Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      color:
                                        globalColours['$global-colour-interactive-primary-default'],
                                    }}
                                  >
                                    <Typography variant='body3SemiBold'>
                                      {sortBy === 'EARLIEST' ? 'Earliest' : 'Latest'}
                                    </Typography>
                                    <ArrowLeftIcon
                                      sx={{
                                        width: '24px',
                                        height: '24px',
                                        transform: 'rotate(-90deg)',
                                      }}
                                    />
                                  </Box>
                                </Box>

                                <Box sx={{ display: 'flex', gap: 1 }}>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      py: 1,
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleScroll(group.name, 'left')}
                                  >
                                    <ArrowLeftIcon
                                      sx={{
                                        width: '20px',
                                        height: '20px',
                                      }}
                                    />
                                    <Typography variant='body3'>Previous</Typography>
                                  </Box>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 0.5,
                                      py: 1,
                                      borderRadius: '8px',
                                      cursor: 'pointer',
                                    }}
                                    onClick={() => handleScroll(group.name, 'right')}
                                  >
                                    <Typography variant='body3'>Next</Typography>
                                    <ArrowLeftIcon
                                      sx={{
                                        width: '20px',
                                        height: '20px',
                                        transform: 'rotate(180deg)',
                                      }}
                                    />
                                  </Box>
                                </Box>
                              </Box>
                            </Box>
                          </Box>

                          <Box sx={{ position: 'relative', mt: 2 }}>
                            {/* Scrollable Products Container */}
                            <Box
                              ref={el => {
                                if (el) {
                                  scrollContainerRefs.current[group.name] = el as HTMLElement;
                                }
                              }}
                              sx={{
                                display: 'flex',
                                gap: 2,
                                overflowX: 'auto',
                                scrollBehavior: 'smooth',
                                pb: 2,
                                alignItems: 'center',
                                '&::-webkit-scrollbar': {
                                  display: 'none',
                                },
                                scrollbarWidth: 'none',
                              }}
                            >
                              {!getProducts(group?.name)?.data?.length ? (
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'center',
                                    alignItems: 'center',
                                    my: 4,
                                    mx: 'auto',
                                    color: globalColours['$global-colour-text-secondary'],
                                  }}
                                >
                                  <Typography variant='body2'>
                                    No classes found for this subject group
                                  </Typography>
                                </Box>
                              ) : (
                                <>
                                  {getProducts(group?.name)?.data?.map(product => (
                                    <ProductCard
                                      key={product?.id}
                                      product={product}
                                      gradeLevels={gradeLevels}
                                      onNavigateToClass={handleNavigateToClass}
                                      onSetUserProfile={handleSetUserProfile}
                                    />
                                  ))}
                                </>
                              )}
                            </Box>
                            {!!getProducts(group?.name)?.data?.length && (
                              <Pagination
                                sx={{ mx: 'auto', my: 5, width: 'fit-content' }}
                                currentPage={getCurrentPage(group?.name)}
                                handleClick={page => handlePageChange(group.name, page)}
                                numPages={
                                  getProducts(group?.name)?.data?.[0]?.pagination?.pages || 1
                                }
                              />
                            )}
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>

                  <Box sx={{ height: '56px', display: { xs: 'none', md: 'block' } }} />
                </Box>
              )}
            </Box>
          </Box>

          <Pitch
            background={globalColours['$global-colour-palette-gray-50']}
            pb={9}
            hideArrow
            showTopImage
            {...extractPitchSection()}
          />

          <Footer {...extractFooterContent(data)} />
        </>
      )}

      <Sheet
        sx={{ zIndex: 101 }}
        closeSheet={handleCloseSheet}
        openSheet={!!expandedText}
        body={<Typography variant='body2'>{expandedText}</Typography>}
      />

      <Sheet
        sx={{ zIndex: 101 }}
        closeSheet={clearUserProfile}
        openSheet={!!userProfile}
        body={<UserProfile {...userProfile} />}
      />

      {(expandedText || !!userProfile) && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 5,
            backgroundColor: globalColours['$global-colour-background-dimmer'],
          }}
          onClick={() => {
            handleCloseSheet();
            clearUserProfile();
          }}
        ></Box>
      )}
    </>
  );
};

Curriculum.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
};

Curriculum.defaultProps = {
  children: null,
  className: null,
};

export default styled<any>(Curriculum)(
  ({ theme }) => `
`,
);
